import { ArrowLeft, LogIn, Mail } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Field } from "../components/Field.js";
import { Action, ErrorLine, LinkAction, Screen } from "../components/Screen.js";
import { signUp } from "../services/authService.js";

/**
 * Create an account.
 *
 * # The answer is always the same
 *
 * ADR 0010: signing up with an address that already has an account resolves
 * **identically** to signing up with one that does not — what differs is the
 * message that lands in the inbox. So this screen shows one outcome, "check your
 * email", and must never try to be more helpful than that; "this address is
 * already registered" is the exact leak the server refuses to produce.
 *
 * # Verification is not a gate. Approval is.
 *
 * An unverified account can sign in and play (`email_verified` is reported, not
 * enforced). **An unapproved one cannot do either** — 19a refuses to mint a
 * session for it at both doors — so the confirmation screen says so plainly
 * rather than offering a way in that will bounce.
 *
 * # The second path (finding 2.4), and why it is a button rather than a form
 *
 * Registration by link already exists: `POST /api/auth/link` creates the account
 * when the address has none, under the same approval gate. What was missing was
 * anybody being told, so this screen points at it.
 *
 * ⚠️ **Not a second form on this screen.** `SignInLinkPage` records what that
 * looked like when it was tried: three inputs, two of them asking for an email
 * address, and no way to tell which one you were meant to fill in. Two ways of
 * getting an account are two screens.
 */
/**
 * Mirrors `password::MIN_LENGTH` in the Rust.
 *
 * Kept the *same*, not stricter: a client that refuses a password the server
 * would accept is a bug somebody experiences as "it won't let me sign up", and
 * client-side validation here is for a faster error rather than a safer one.
 */
const MIN_PASSWORD = 10;

/** Characters, the way the server counts them — `.length` counts UTF-16 units. */
function longEnough(password: string): boolean {
  return [...password].length >= MIN_PASSWORD;
}

export function RegisterPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    void (async () => {
      try {
        await signUp({
          email: email.trim(),
          password,
          display_name: name.trim(),
        });
        setSent(true);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : t("register.failed"));
      } finally {
        setBusy(false);
      }
    })();
  };

  if (sent) {
    return (
      <Screen
        title={t("register.sent.title")}
        lead={t("register.sent.lead", { email: email.trim() })}
        footer={
          <LinkAction to="/login" icon={LogIn} accent>
            {t("login.title")}
          </LinkAction>
        }
      >
        <p className="text-sm text-white/60">{t("register.sent.body")}</p>
      </Screen>
    );
  }

  return (
    <Screen
      title={t("register.title")}
      lead={t("register.lead")}
      footer={
        // Buttons, matching the door and `/login` — phase 20a.
        <>
          <LinkAction to="/login" icon={LogIn} accent>
            {t("register.haveOne")}
          </LinkAction>
          {/* Finding 2.4. The link path creates the account too, and it is the
              kinder one on a phone — no password to invent at a party. */}
          <LinkAction to="/signin" icon={Mail}>
            {t("register.useLink")}
          </LinkAction>
          <LinkAction to="/" icon={ArrowLeft}>
            {t("common.back")}
          </LinkAction>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field
          id="name"
          label={t("register.nameLabel")}
          value={name}
          onChange={setName}
          autoComplete="nickname"
          autoCapitalize="words"
          maxLength={40}
          required
          hint={t("register.nameHint")}
        />
        <Field
          id="email"
          label={t("common.email")}
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          inputMode="email"
          autoCapitalize="none"
          required
        />
        <Field
          id="password"
          label={t("common.password")}
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          minLength={MIN_PASSWORD}
          required
          hint={t("register.passwordHint", { min: MIN_PASSWORD })}
        />
        {error && <ErrorLine>{error}</ErrorLine>}
        <Action disabled={busy || !email.trim() || !name.trim() || !longEnough(password)}>
          {t("register.action")}
        </Action>
      </form>
    </Screen>
  );
}
